import PropTypes from "prop-types"
import classNames from "classnames"
import { useState } from "react"
import type { KeyboardEvent } from "react"
import { AutoAwesomeOutlined, DownloadOutlined } from "@mui/icons-material"
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { formatLastUpdated } from "connected-components/skills/utils/format"
import { useSkillDownload } from "connected-components/skills/hooks/use-skill-download"

import "./style.scss"

interface SkillCardProps {
  skill: SkillCatalogSkill
}

function SkillCard({ skill }: SkillCardProps) {
  const lastUpdated = formatLastUpdated(skill.lastUpdated)
  const versionLabel = skill.version ? `v${skill.version}` : null
  const metaParts = [versionLabel, lastUpdated && `Updated ${lastUpdated}`].filter(Boolean)
  const { downloadSkill, isDownloading } = useSkillDownload(skill)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  function handleConfirmDownload() {
    downloadSkill()
    setConfirmOpen(false)
  }

  function toggleDescription() {
    setDescriptionExpanded(current => !current)
  }

  function handleDescriptionKeyDown(event: KeyboardEvent<HTMLParagraphElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      toggleDescription()
    }
  }

  return (
    <Card square className="skill-card">
      <div className="skill-card-layout">
        <div className="skill-card-icon">
          <AutoAwesomeOutlined fontSize="medium" />
        </div>

        <div className="skill-card-content">
          <div className="skill-card-heading">
            <Typography variant="h6" component="h3" className="skill-card-title">
              {skill.name}
            </Typography>
            {skill.beta && (
              <Chip size="small" color="info" variant="outlined" label="Preview" className="skill-card-beta" />
            )}
          </div>

          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            className={classNames("skill-card-description", {
              "skill-card-description--expanded": descriptionExpanded,
            })}
            role="button"
            tabIndex={0}
            aria-expanded={descriptionExpanded}
            onClick={toggleDescription}
            onKeyDown={handleDescriptionKeyDown}
          >
            {skill.description}
          </Typography>

          {metaParts.length > 0 && (
            <Typography variant="caption" color="textSecondary" className="skill-card-meta">
              {metaParts.join(" · ")}
            </Typography>
          )}
        </div>

        <div className="skill-card-actions">
          <Tooltip title="Download workflow">
            <span>
              <IconButton
                size="small"
                color="primary"
                className="skill-card-download"
                aria-label={`Download ${skill.name}`}
                disabled={isDownloading}
                loading={isDownloading}
                onClick={() => setConfirmOpen(true)}
              >
                <DownloadOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Download {skill.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will download the agentic workflow file to your device.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmDownload}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

SkillCard.propTypes = {
  skill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    tier: PropTypes.oneOf(["licensed", "proprietary"]).isRequired,
    plugin: PropTypes.string,
    author: PropTypes.string,
    downloadFilename: PropTypes.string,
    lastUpdated: PropTypes.string,
    beta: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
}

export default SkillCard
